import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faXmark } from '@fortawesome/free-solid-svg-icons';

export const ActionsDropdownItem = ({ children, onClick, className = '' }) => (
    <div className={`actions-dropdown-item${className ? ` ${className}` : ''}`} onClick={onClick}>
        {children}
    </div>
);

export const ActionsDropdownDivider = ({ className = '' }) => (
    <div className={`actions-dropdown-divider${className ? ` ${className}` : ''}`} role="separator" />
);

/**
 * Shared table row actions menu.
 * Controlled via isOpen / onOpenChange so a parent can keep only one menu open.
 * Renders as a centered sheet with a modal-style backdrop so items are never clipped.
 */
export const ActionsDropdown = ({
    isOpen,
    onOpenChange,
    menuWidth,
    label = 'Actions',
    title = 'Actions',
    buttonClassName = 'btn btn-action btn-secondary actions-dropdown-btn',
    children,
    className = '',
    // Kept for call-site compatibility; positioning is no longer used.
    portal: _portal = true,
    estimatedMenuHeight: _estimatedMenuHeight,
}) => {
    const menuRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (event) => {
            // Let nested form modals own Escape while they are open
            if (event.key === 'Escape' && !document.querySelector('.modal-overlay')) {
                onOpenChange(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onOpenChange]);

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) {
            onOpenChange(false);
        }
    };

    const menuStyle = menuWidth
        ? { width: menuWidth, minWidth: menuWidth }
        : undefined;

    return (
        <div className={`actions-dropdown-container${className ? ` ${className}` : ''}`}>
            <button
                type="button"
                className={buttonClassName}
                onClick={() => onOpenChange(!isOpen)}
                title={title}
            >
                <FontAwesomeIcon icon={faEllipsisV} />
                {label ? <span>{label}</span> : null}
            </button>
            {isOpen &&
                createPortal(
                    <div
                        className="actions-dropdown-overlay"
                        onClick={handleOverlayClick}
                        role="presentation"
                    >
                        <div
                            ref={menuRef}
                            className="actions-dropdown-menu actions-dropdown-sheet"
                            style={menuStyle}
                            role="menu"
                            aria-label={title}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="actions-dropdown-sheet-header">
                                <h3 className="actions-dropdown-sheet-title">{title}</h3>
                                <button
                                    type="button"
                                    className="actions-dropdown-sheet-close"
                                    onClick={() => onOpenChange(false)}
                                    aria-label="Close actions"
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </div>
                            <div className="actions-dropdown-sheet-body">
                                {children}
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};
